/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ActionButton, BrandColors, Modal, Text } from '@blend/enterprise-components';
import { DateTime } from 'luxon';
import { notaryAPI } from '../../api/notary';
import { DocumentsAPI } from '../../api/common/documents';
import { useRootSelector } from '../../hooks/useRootSelector';
import { ModalContent } from '@components/common/enterprise/ModalContent/ModalContent';
import { redirectToURL } from '@utils/routing';
import { parseException } from '@utils/error';
import { formatElapsedTime } from '@utils/formatting';
import { RonEnvelopeStatus } from '@shared/constants/document';

const styles = StyleSheet.create({
  innerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 28,
  },
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 28,
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
    marginTop: 36,
  },
  submitWrapper: {
    marginLeft: 8,
  },
  spinnerWrapper: {
    marginTop: 28,
    display: 'flex',
    justifyContent: 'center',
  },
});

export namespace TaggingModal {
  export interface Props {
    roomId: string;
    handleClose: () => void;
  }
}

function formatLastStatusUpdatedElapsedTIme(envelope: DocumentsAPI.EnvelopeReference): string {
  return formatElapsedTime(DateTime.fromISO(envelope.lastStatusUpdatedAt), { unitsToShow: 1 });
}

/**
 * This modal opens after the user clicks on "Tag" button from DocumentManagement.
 * First, it checks current tagging status then show error/warning message (ex: the other user is tagging).
 * After the user confirms, it sends request to get the tagging URL and redirects the browser to that URL
 * */
export const TaggingModal: React.FC<TaggingModal.Props> = ({ roomId, handleClose }) => {
  const user = useRootSelector(state => state.user);
  const [loadingMessage, setLoadingMessage] = React.useState<string>();
  const [warning, setWarning] = useState<string>();
  const [error, setError] = React.useState<string>();
  const [canCloseModal, setCanCloseModal] = useState(true);

  const fetchTaggingURLAndRedirect = async (): Promise<void> => {
    try {
      setError(undefined);
      setWarning(undefined);
      setLoadingMessage('Preparing documents for tagging...');
      // don't allows user to close the modal after starting to fetch loading URL
      setCanCloseModal(false);
      const { url } = await notaryAPI.documents.fetchRoomDocumentEditUrl(roomId);
      redirectToURL(url);
    } catch (exception) {
      const { message } = parseException(exception, { notify: true });
      setError(message);
      setLoadingMessage(undefined);
      setCanCloseModal(true);
    }
  };

  useEffect(() => {
    async function checkTaggingStatus() {
      setError(undefined);
      setLoadingMessage('Check tagging status...');
      let envelopes: DocumentsAPI.EnvelopeReference[];
      try {
        envelopes = await notaryAPI.rooms.fetchEnvelopeReferences(roomId);
      } catch (exception) {
        const { message } = parseException(exception, { notify: true });
        setError(message);
        return;
      } finally {
        setLoadingMessage(undefined);
      }

      // when there is a post-processing of tagging (for the same user or different user), UI displays an error message
      // but user can still click on "Tag documents" button to retry fetching redirect URL.
      // In the backend, there is a check to prevent the user from retrying too soon.
      const finalizingEnvelope = envelopes.find(envelope => envelope.status === RonEnvelopeStatus.FINALIZING_TAGGING);
      if (finalizingEnvelope) {
        const elapsed = formatLastStatusUpdatedElapsedTIme(finalizingEnvelope);
        setError(
          `The post-processing for user ${finalizingEnvelope.notaryName} started ${elapsed}. Please wait and try again later.`,
        );
        return;
      }

      // when another user started the tagging and has not clicked on Save/Close, shows a warning message to user
      const inProgressEnvelopeOfAnotherUser = envelopes.find(
        envelope => envelope.notaryId !== user.id && envelope.status === RonEnvelopeStatus.TAGGING_IN_PROGRESS,
      );
      if (inProgressEnvelopeOfAnotherUser) {
        const elapsed = formatLastStatusUpdatedElapsedTIme(inProgressEnvelopeOfAnotherUser);
        setWarning(
          `User ${inProgressEnvelopeOfAnotherUser.notaryName} started tagging ${elapsed}. You can tag documents but your work will override ${inProgressEnvelopeOfAnotherUser.notaryName}'s work.`,
        );
        return;
      }

      await fetchTaggingURLAndRedirect();
    }

    void checkTaggingStatus();
  }, [roomId]);

  const handleCloseModal = () => {
    // Once the user clicks on "Tag documents" button, the modal cannot be cancelled (unless there is an error)
    // While the button is already disabled, we still need noop here for the case user clicks mouse outside of the modal
    if (!canCloseModal) {
      return;
    }
    handleClose();
  };

  return (
    <Modal data-it="RescheduleModal" contentLabel="Tag documents" handleClose={handleCloseModal} isOpen={true}>
      <ModalContent handleClose={canCloseModal ? handleCloseModal : undefined} headerText="Tag documents">
        <div>
          <div data-it="tagDocument-messages" className={css(styles.innerWrapper)}>
            {warning && (
              <div>
                <Text fontSize="md" colorName="NEUTRAL_800">
                  {warning}
                </Text>
              </div>
            )}

            {error && (
              <div>
                <Text colorName="ACCENT_RED" fontSize="md">
                  {error}
                </Text>
              </div>
            )}

            {loadingMessage && (
              <div>
                <Text fontSize="md" colorName="NEUTRAL_800">
                  {loadingMessage}
                </Text>
              </div>
            )}
          </div>

          <div className={css(styles.footerBar)}>
            <div className={css(styles.submitWrapper)}>
              <ActionButton
                data-dd-privacy="allow"
                disabled={false}
                isLoading={loadingMessage !== undefined}
                data-it="tagDocument-submit"
                label="Tag documents"
                onClick={fetchTaggingURLAndRedirect}
              />
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};
